import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { Text, Pill, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { StageProgress } from "@services/types/programming-edit/programmingEditTypes";
import { useProgrammingEditStore } from "@context/useProgrammingEditStore";
import StageProgressBar from "../stage-progress-bar";
import { hasStageDependencyViolation } from "../../utils/stageDependency";
import styles from "./stageProgressCard.module.scss";
import { blocksToHoursLabel } from "@utils/blocks";

interface StageProgressCardProps {
  stage: StageProgress;
}

const StageProgressCard: React.FC<StageProgressCardProps> = ({ stage }) => {
  const { t } = useTranslation();
  const slots = useProgrammingEditStore(s => s.slots);
  const stages = useProgrammingEditStore(s => s.stages);
  const isOverAssigned = stage.assignedBlocks > stage.totalBlocks;

  /**
   * Checks whether this stage violates a dependency constraint, i.e.
   * it has slots scheduled on or before the latest date of a dependency stage.
   */
  const hasDependencyViolation = useMemo(() => {
    const adaptedStage = stages.find(s => s.id === stage.id);
    if (!adaptedStage?.dependencyCodes?.length) return false;

    const currentSlots = slots.filter(s => s.stageId === stage.id);
    if (currentSlots.length === 0) return false;

    const stageByCode = new Map(stages.map(s => [s.code, s]));

    return adaptedStage.dependencyCodes.some(depCode => {
      const depStage = stageByCode.get(depCode);
      if (!depStage) return false;

      const depSlots = slots.filter(s => s.stageId === depStage.id);
      return hasStageDependencyViolation(currentSlots, depSlots);
    });
  }, [stage.id, slots, stages]);

  const variant = useMemo(() => {
    if (isOverAssigned) {
      return "error";
    }

    if (hasDependencyViolation) {
      return "partial";
    }

    if (stage.isComplete) {
      return "complete";
    }

    if (stage.percentage > 0) {
      return "partial";
    }

    return "empty";
  }, [stage.isComplete, stage.percentage, isOverAssigned, hasDependencyViolation]);

  return (
    <div className={cx(styles.stageProgressCard, styles[`stageProgressCard__${variant}`])}>
      <div className={styles.stageProgressCard_header}>
        <Text weight="medium" className={styles.stageProgressCard_name}>
          {stage.name}
        </Text>
        {stage.isComplete && !hasDependencyViolation && (
          <Icon
            className={styles.stageProgressCard_checkIcon}
            name="circle-check"
            filled
            color="success-50"
          />
        )}
      </div>

      <div className={styles.stageProgressCard_progress}>
        <StageProgressBar percentage={stage.percentage} variant={variant} />

        <div className={styles.stageProgressCard_stats}>
          <Tooltip label={t("programming-edit.stage-card.block-tooltip")}>
            <Text type="sm" className={styles.stageProgressCard_statsDetails}>
              {t("programming-edit.stage-card.blocks-detail", {
                assigned: stage.assignedBlocks,
                total: stage.totalBlocks,
                assignedHours: stage.assignedHours,
                totalHours: blocksToHoursLabel(stage.totalBlocks),
              })}
            </Text>
          </Tooltip>

          <Text type="sm" weight="medium" className={styles.stageProgressCard_statsPercentage}>
            {stage.percentage}%
          </Text>
        </div>
      </div>

      {stage?.dependencyLabel && (
        <div className={styles.stageProgressCard_dependency}>
          <Pill>
            {t("programming-edit.stage-card.dependency-label", { name: stage?.dependencyLabel })}
          </Pill>{" "}
          <Tooltip
            className={styles.stageProgressCard_dependencyTooltip}
            label={t("programming-edit.stage-card.dependency-tooltip", {
              name: stage?.dependencyLabel,
            })}
          >
            <Icon name="circle-info" size="sm" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default StageProgressCard;
